import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { toast } from 'sonner';
import { schema } from '../__generated__/schema-lite';
import { useAuth, getAuthToken } from '../auth/AuthContext';

export type FeedAction = 'BOOKED' | 'RESCHEDULED' | 'CANCELLED' | 'LATE' | 'OTHER';

export type FeedItem = {
  id: string;
  action: FeedAction;
  platform: 'WEB' | 'PHONE' | 'SUPERDOC';
  domain: string;
  doctorName: string | null;
  patientName: string | null;
  patientPhone: string | null;
  whenText: string | null;
  oldWhenText: string | null;
  createdAt: string;
};

type Ctx = {
  allowed: boolean;
  items: FeedItem[];
  /** Unseen count EXCLUDING late arrivals — the normal (rose) bell badge. */
  unseenCount: number;
  /** Unseen late-arrival count — its own (red) bell badge, kept separate so
   *  "a patient is running late" stands out from routine (blue) book/cancel traffic. */
  unseenLateCount: number;
  isUnseen: (n: FeedItem) => boolean;
  markSeen: () => void;
  /** Register a listener fired for every live incoming event (the same ones that
   *  update the feed). Returns an unsubscribe. Used by the calendar pages to
   *  refresh themselves on a new/changed appointment while they're open. */
  subscribe: (cb: (n: FeedItem) => void) => () => void;
};

const NotificationsCtx = createContext<Ctx | null>(null);

export function useNotifications(): Ctx {
  const ctx = useContext(NotificationsCtx);
  if (!ctx) throw new Error('useNotifications must be used inside <NotificationsProvider>');
  return ctx;
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const crud = useClientQueries(schema);
  const queryClient = useQueryClient();

  const { data: me } = crud.$procs.me.useQuery(undefined, { enabled: !!token });
  const role = (me as { role?: string } | undefined)?.role;
  const allowed = role === 'ADMIN' || role === 'SUPERDOC';

  const listQ = crud.activityEvent.useFindMany(
    { orderBy: { createdAt: 'desc' }, take: 30 },
    { enabled: allowed },
  );
  const items = useMemo(() => (listQ.data ?? []) as unknown as FeedItem[], [listQ.data]);

  const seenRaw = (me as { notificationsSeenAt?: string | null } | undefined)?.notificationsSeenAt;
  const seenAt = seenRaw ? new Date(seenRaw).getTime() : null;

  const isUnseen = useCallback(
    (n: FeedItem) => (seenAt == null ? true : new Date(n.createdAt).getTime() > seenAt),
    [seenAt],
  );
  // Late arrivals get their own badge, so split the unseen tally in two.
  const unseenCount = useMemo(
    () => items.filter((n) => n.action !== 'LATE' && isUnseen(n)).length,
    [items, isUnseen],
  );
  const unseenLateCount = useMemo(
    () => items.filter((n) => n.action === 'LATE' && isUnseen(n)).length,
    [items, isUnseen],
  );

  // --- live SSE stream ---------------------------------------------------
  const refetchRef = useRef(listQ.refetch);
  refetchRef.current = listQ.refetch;
  const toastedRef = useRef<Set<string>>(new Set());
  // Page-level listeners (e.g. an open calendar) that want each live event.
  const subscribersRef = useRef<Set<(n: FeedItem) => void>>(new Set());
  const subscribe = useCallback((cb: (n: FeedItem) => void) => {
    subscribersRef.current.add(cb);
    return () => {
      subscribersRef.current.delete(cb);
    };
  }, []);

  const onIncoming = useCallback(
    (n: FeedItem) => {
      refetchRef.current?.();
      subscribersRef.current.forEach((cb) => cb(n));
      if (toastedRef.current.has(n.id)) return;
      toastedRef.current.add(n.id);
      const title = t(`enums.${n.action}`, n.action);
      const who =
        n.domain === 'rehab'
          ? n.patientName ?? ''
          : [n.doctorName, n.patientName].filter(Boolean).join(' · ');
      const when =
        n.action === 'RESCHEDULED' && n.oldWhenText
          ? `${n.oldWhenText} → ${n.whenText ?? ''}`
          : n.whenText ?? n.patientPhone ?? '';
      toast(title, { description: [who, when].filter(Boolean).join(' · ') || undefined });
    },
    [t],
  );

  useEffect(() => {
    if (!allowed || !token) return;
    let aborted = false;
    let controller: AbortController | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      controller = new AbortController();
      try {
        const res = await fetch('/api/notifications/stream', {
          headers: { Authorization: `Bearer ${getAuthToken() ?? token}` },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const frames = buf.split('\n\n');
          buf = frames.pop() ?? '';
          for (const frame of frames) {
            const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
            if (!dataLine) continue;
            try {
              onIncoming(JSON.parse(dataLine.slice(5).trim()) as FeedItem);
            } catch {
              /* ignore malformed frame */
            }
          }
        }
      } catch {
        /* network/abort — reconnect below */
      }
      if (!aborted) retry = setTimeout(connect, 3000);
    };
    connect();

    return () => {
      aborted = true;
      if (retry) clearTimeout(retry);
      controller?.abort();
    };
  }, [allowed, token, onIncoming]);

  // --- mark seen ---------------------------------------------------------
  const markSeenMut = crud.$procs.markNotificationsSeen.useMutation();
  const markSeen = useCallback(() => {
    if (unseenCount === 0 && unseenLateCount === 0) return;
    markSeenMut.mutate(undefined as never, { onSuccess: () => queryClient.invalidateQueries() });
  }, [markSeenMut, queryClient, unseenCount, unseenLateCount]);

  const value = useMemo<Ctx>(
    () => ({ allowed, items, unseenCount, unseenLateCount, isUnseen, markSeen, subscribe }),
    [allowed, items, unseenCount, unseenLateCount, isUnseen, markSeen, subscribe],
  );

  return <NotificationsCtx.Provider value={value}>{children}</NotificationsCtx.Provider>;
}
